import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Search, ShieldCheck, Users as UsersIcon, KeyRound, Power,
  Layers, Lock, ShieldAlert, UserPlus, ArrowRightLeft, UserRound,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../lib/format';
import { MODULES, LEVELS, LEVEL_LABEL, SCOPE_LABEL } from '../lib/modules';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { EmptyState } from '../components/common';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const LEVEL_TONE = {
  deny: 'text-muted-foreground',
  read: 'text-[hsl(var(--teal))]',
  readwrite: 'text-[hsl(var(--brand))]',
  approve: 'text-[hsl(var(--success))]',
};

export default function Users() {
  const { user, can } = useAuth();
  const canManage = can('users', 'readwrite');
  const [tab, setTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const [u, g] = await Promise.all([api.get('/users'), api.get('/groups')]);
      setUsers(u.data); setGroups(g.data);
    } catch { toast.error('Could not load access data'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groupName = (id) => groups.find((g) => g.id === id)?.name || '—';

  // ---------------- User editor ----------------
  const [uOpen, setUOpen] = useState(false);
  const [uEditing, setUEditing] = useState(null);
  const [uForm, setUForm] = useState({ name: '', email: '', password: '', groupId: '', active: true });

  const openNewUser = () => {
    const def = groups.find((g) => g.name === 'Estimator') || groups[0];
    setUEditing(null);
    setUForm({ name: '', email: '', password: '', groupId: def?.id || '', active: true });
    setUOpen(true);
  };
  const openEditUser = (u) => {
    setUEditing(u);
    setUForm({ name: u.name, email: u.email, password: '', groupId: u.groupId || '', active: u.active });
    setUOpen(true);
  };
  const saveUser = async () => {
    try {
      if (uEditing) {
        const payload = { name: uForm.name, groupId: uForm.groupId, active: uForm.active };
        if (uForm.password) payload.password = uForm.password;
        await api.put(`/users/${uEditing.id}`, payload);
        toast.success('User updated');
      } else {
        if (!uForm.name || !uForm.email || !uForm.password || !uForm.groupId) {
          toast.error('Name, User ID, password and group are required'); return;
        }
        await api.post('/users', uForm);
        toast.success('User created');
      }
      setUOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const toggleUserActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? Consider disabling instead.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('User deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // ---------------- Group editor ----------------
  const emptyPerms = () => Object.fromEntries(MODULES.map((m) => [m.key, 'deny']));
  const [gOpen, setGOpen] = useState(false);
  const [gEditing, setGEditing] = useState(null);
  const [gForm, setGForm] = useState({ name: '', description: '', scope: 'own', approvalLimitAed: '', permissions: emptyPerms() });

  const openNewGroup = () => {
    setGEditing(null);
    setGForm({ name: '', description: '', scope: 'own', approvalLimitAed: '', permissions: { ...emptyPerms(), dashboard: 'read' } });
    setGOpen(true);
  };
  const openEditGroup = (g) => {
    setGEditing(g);
    setGForm({
      name: g.name, description: g.description || '', scope: g.scope || 'own',
      approvalLimitAed: g.approvalLimit != null ? String(g.approvalLimit / 100) : '',
      permissions: { ...emptyPerms(), ...(g.permissions || {}) },
    });
    setGOpen(true);
  };
  const setPerm = (moduleKey, level) => setGForm((f) => ({ ...f, permissions: { ...f.permissions, [moduleKey]: level } }));
  const saveGroup = async () => {
    try {
      const approvalLimit = gForm.approvalLimitAed !== '' ? Math.round(Number(gForm.approvalLimitAed) * 100) : null;
      const payload = {
        name: gForm.name, description: gForm.description, scope: gForm.scope,
        approvalLimit, permissions: gForm.permissions,
      };
      if (gEditing) { await api.put(`/groups/${gEditing.id}`, payload); toast.success('Group updated'); }
      else {
        if (!gForm.name.trim()) { toast.error('Group name is required'); return; }
        await api.post('/groups', payload); toast.success('Group created');
      }
      setGOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const removeGroup = async (g) => {
    if (!window.confirm(`Delete the "${g.name}" group?`)) return;
    try { await api.delete(`/groups/${g.id}`); toast.success('Group deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // ---------------- Per-group Members panel ----------------
  // Each user belongs to exactly one group, so "add" = move a user INTO this group
  // and "remove" = move them OUT to another group. Reuses PUT /users/{id}.
  const [membersGroupId, setMembersGroupId] = useState(null);
  const [addPick, setAddPick] = useState('');
  const membersGroup = groups.find((g) => g.id === membersGroupId) || null;
  const groupMembers = users.filter((u) => u.groupId === membersGroupId);
  const nonMembers = users.filter((u) => u.groupId !== membersGroupId);

  const openMembers = (g) => { setAddPick(''); setMembersGroupId(g.id); };
  const moveUserToGroup = async (u, groupId, label) => {
    try {
      await api.put(`/users/${u.id}`, { groupId });
      toast.success(`${u.name} → ${label}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not move user'); }
  };
  const addMember = async () => {
    if (!addPick || !membersGroup) return;
    const u = users.find((x) => x.id === addPick);
    if (u) await moveUserToGroup(u, membersGroup.id, membersGroup.name);
    setAddPick('');
  };

  const filteredUsers = users.filter((u) => `${u.name} ${u.email} ${groupName(u.groupId)}`.toLowerCase().includes(q.toLowerCase()));
  const isSystemGroup = !!gEditing?.isSystem;

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">User &amp; Access Management</h1>
          <p className="text-sm text-muted-foreground">Create accounts, assign access groups, and define per-module permissions.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon size={14} className="mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups"><Layers size={14} className="mr-1.5" />Access Groups</TabsTrigger>
        </TabsList>

        {/* ---------------- USERS ---------------- */}
        <TabsContent value="users" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative max-w-sm flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-9" data-testid="users-search" />
            </div>
            {canManage && (
              <Button data-testid="add-user-button" onClick={openNewUser} className="bg-[hsl(var(--brand))] text-white hover:brightness-110">
                <Plus size={15} className="mr-2" />Add User
              </Button>
            )}
          </div>

          {filteredUsers.length === 0 ? (
            <EmptyState icon={UsersIcon} title="No users found" description="Add team members and assign their access group."
              action={canManage ? <Button onClick={openNewUser}><Plus size={15} className="mr-2" />Add User</Button> : null} testid="users-empty" />
          ) : (
            <div className="erak-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead><tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">User ID</th>
                    <th className="px-4 py-3 font-semibold">Access Group</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Created</th>
                    <th className="px-4 py-3" />
                  </tr></thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="px-4 py-2.5 font-medium">{u.name}{u.id === user.id && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">you</span>}</td>
                        <td className="px-4 py-2.5 font-num text-[hsl(var(--copper))]">{u.email}</td>
                        <td className="px-4 py-2.5"><Badge variant="secondary" className="text-xs">{u.groupName || groupName(u.groupId)}</Badge></td>
                        <td className="px-4 py-2.5">
                          <button onClick={() => canManage && toggleUserActive(u)} disabled={!canManage}
                            className={`inline-flex items-center gap-1.5 text-xs ${u.active ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
                            <Power size={13} />{u.active ? 'Active' : 'Disabled'}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(u.createdAt)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {canManage && (
                            <div className="flex justify-end gap-1">
                              <button onClick={() => openEditUser(u)} className="rounded-lg p-1.5 hover:bg-muted" data-testid={`user-edit-${u.email}`}><Pencil size={14} /></button>
                              <button onClick={() => removeUser(u)} className="rounded-lg p-1.5 text-[hsl(var(--danger))] hover:bg-muted" data-testid={`user-delete-${u.email}`}><Trash2 size={14} /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---------------- GROUPS ---------------- */}
        <TabsContent value="groups" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Each user belongs to exactly one group. Permissions and record visibility derive entirely from the group.</p>
            {canManage && (
              <Button data-testid="add-group-button" onClick={openNewGroup}><Plus size={15} className="mr-2" />New Group</Button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {groups.map((g) => {
              const granted = MODULES.filter((m) => (g.permissions?.[m.key] || 'deny') !== 'deny');
              return (
                <div key={g.id} className="erak-card flex flex-col gap-3 p-4" data-testid={`group-card-${g.name}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="grid h-9 w-9 place-items-center rounded-lg border bg-muted/40">
                        {g.isSystem ? <ShieldCheck size={17} className="text-[hsl(var(--brand))]" /> : <Layers size={17} className="text-[hsl(var(--teal))]" />}
                      </span>
                      <div>
                        <div className="flex items-center gap-1.5 text-sm font-semibold">{g.name}
                          {g.isSystem && <Badge variant="outline" className="gap-1 text-[10px]"><Lock size={9} />System</Badge>}</div>
                        <div className="text-[11px] text-muted-foreground">{g.memberCount} member{g.memberCount === 1 ? '' : 's'} · {SCOPE_LABEL[g.scope] || g.scope}</div>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <button onClick={() => openMembers(g)} className="rounded-lg p-1.5 hover:bg-muted" data-testid={`group-members-${g.name}`} title="Manage members"><UsersIcon size={14} /></button>
                        <button onClick={() => openEditGroup(g)} className="rounded-lg p-1.5 hover:bg-muted" data-testid={`group-edit-${g.name}`}><Pencil size={14} /></button>
                        {!g.isSystem && <button onClick={() => removeGroup(g)} className="rounded-lg p-1.5 text-[hsl(var(--danger))] hover:bg-muted" data-testid={`group-delete-${g.name}`}><Trash2 size={14} /></button>}
                      </div>
                    )}
                  </div>
                  {g.description && <p className="text-xs text-muted-foreground">{g.description}</p>}
                  {canManage && (
                    <button onClick={() => openMembers(g)} data-testid={`group-members-link-${g.name}`}
                      className="-mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-[hsl(var(--teal))] hover:underline">
                      <UsersIcon size={11} />View &amp; manage members
                    </button>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {granted.length === 0 ? <span className="text-[11px] text-muted-foreground">No module access</span> : granted.map((m) => (
                      <span key={m.key} className="rounded-md border bg-muted/30 px-1.5 py-0.5 text-[10px]">
                        {m.label.split(' ')[0]} <span className={LEVEL_TONE[g.permissions[m.key]]}>· {g.permissions[m.key]}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* User sheet */}
      <Sheet open={uOpen} onOpenChange={setUOpen}>
        <SheetContent data-testid="user-sheet" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader><SheetTitle>{uEditing ? `Edit ${uEditing.name}` : 'Add User'}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Full name</Label>
              <Input data-testid="user-name-input" value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} placeholder="e.g. Sibin Kumar" /></div>
            <div className="space-y-1.5"><Label>User ID (login)</Label>
              <Input data-testid="user-email-input" value={uForm.email} disabled={!!uEditing} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} placeholder="e.g. sibin@erak" className="font-num" />
              {uEditing && <div className="text-[10px] text-muted-foreground">User ID cannot be changed after creation.</div>}</div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><KeyRound size={13} />{uEditing ? 'Reset password (optional)' : 'Password'}</Label>
              <Input data-testid="user-password-input" type="text" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} placeholder={uEditing ? 'Leave blank to keep current' : 'Set an initial password'} className="font-num" /></div>
            <div className="space-y-1.5"><Label>Access group</Label>
              <Select value={uForm.groupId} onValueChange={(v) => setUForm({ ...uForm, groupId: v })}>
                <SelectTrigger data-testid="user-group-select"><SelectValue placeholder="Select a group" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground">{groups.find((g) => g.id === uForm.groupId)?.description}</div>
            </div>
            {uEditing && (
              <div className="flex items-center justify-between rounded-xl border p-3">
                <div><div className="text-sm font-medium">Account active</div><div className="text-xs text-muted-foreground">Disabled users cannot sign in.</div></div>
                <Switch checked={uForm.active} onCheckedChange={(v) => setUForm({ ...uForm, active: v })} data-testid="user-active-toggle" />
              </div>
            )}
          </div>
          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setUOpen(false)}>Cancel</Button>
            <Button data-testid="user-save-button" onClick={saveUser}>{uEditing ? 'Save changes' : 'Create user'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Group sheet */}
      <Sheet open={gOpen} onOpenChange={setGOpen}>
        <SheetContent data-testid="group-sheet" className="flex w-full flex-col overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle>{gEditing ? `Edit ${gEditing.name}` : 'New Access Group'}</SheetTitle></SheetHeader>
          {isSystemGroup && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-[hsl(var(--brand)/0.3)] bg-[hsl(var(--brand)/0.08)] p-3 text-xs">
              <ShieldAlert size={15} className="mt-0.5 shrink-0 text-[hsl(var(--brand))]" />
              <span>The Admin group is a protected system group. Its permissions and scope are fixed; only the description can be edited.</span>
            </div>
          )}
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Group name</Label>
              <Input data-testid="group-name-input" value={gForm.name} disabled={isSystemGroup} onChange={(e) => setGForm({ ...gForm, name: e.target.value })} placeholder="e.g. Procurement Officer" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Textarea rows={2} value={gForm.description} onChange={(e) => setGForm({ ...gForm, description: e.target.value })} placeholder="What this group is for" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Record visibility</Label>
                <Select value={gForm.scope} disabled={isSystemGroup} onValueChange={(v) => setGForm({ ...gForm, scope: v })}>
                  <SelectTrigger data-testid="group-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="own">{SCOPE_LABEL.own}</SelectItem>
                    <SelectItem value="all">{SCOPE_LABEL.all}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Approval limit (AED)</Label>
                <Input type="number" min="0" value={gForm.approvalLimitAed} disabled={isSystemGroup}
                  onChange={(e) => setGForm({ ...gForm, approvalLimitAed: e.target.value })} placeholder="Unlimited" className="font-num" data-testid="group-approval-limit" />
                <div className="text-[10px] text-muted-foreground">Stored for future use (not enforced yet).</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Module permissions</Label>
              <div className="rounded-xl border" data-testid="group-matrix">
                {MODULES.map((m, i) => (
                  <div key={m.key} className={`flex items-center justify-between gap-2 px-3 py-2 ${i ? 'border-t' : ''}`}>
                    <span className="text-sm">{m.label}</span>
                    <Select value={gForm.permissions[m.key]} disabled={isSystemGroup} onValueChange={(v) => setPerm(m.key, v)}>
                      <SelectTrigger className="w-44" data-testid={`group-perm-${m.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL_LABEL[l]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setGOpen(false)}>Cancel</Button>
            <Button data-testid="group-save-button" onClick={saveGroup}>{gEditing ? 'Save group' : 'Create group'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Per-group Members panel */}
      <Dialog open={!!membersGroupId} onOpenChange={(o) => !o && setMembersGroupId(null)}>
        <DialogContent data-testid="group-members-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon size={16} className="text-[hsl(var(--teal))]" />
              {membersGroup?.name} — members
              <Badge variant="secondary" className="ml-1 text-[11px]" data-testid="members-count">{groupMembers.length}</Badge>
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-muted-foreground">
            A user belongs to one group at a time. Adding a member moves them into <span className="font-medium">{membersGroup?.name}</span>; removing moves them to another group.
          </p>

          {/* Add member */}
          <div className="flex items-center gap-2" data-testid="members-add-row">
            <Select value={addPick} onValueChange={setAddPick}>
              <SelectTrigger data-testid="members-add-select"><SelectValue placeholder="Add a user to this group…" /></SelectTrigger>
              <SelectContent>
                {nonMembers.length === 0
                  ? <div className="px-2 py-1.5 text-xs text-muted-foreground">Everyone is already in this group</div>
                  : nonMembers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-muted-foreground">· {u.groupName || groupName(u.groupId)}</span></SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="shrink-0" data-testid="members-add-button" disabled={!addPick} onClick={addMember}>
              <UserPlus size={14} className="mr-1.5" />Add
            </Button>
          </div>

          {/* Members list */}
          <div className="max-h-72 space-y-1.5 overflow-y-auto" data-testid="members-list">
            {groupMembers.length === 0 ? (
              <div className="grid place-items-center gap-1 rounded-xl border border-dashed p-6 text-center text-xs text-muted-foreground">
                <UserRound size={18} className="opacity-60" />No members in this group yet.
              </div>
            ) : groupMembers.map((u) => (
              <div key={u.id} data-testid={`member-row-${u.email}`} className="flex items-center gap-2 rounded-xl border p-2">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold">
                  {u.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{u.name}{u.id === user.id && <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">you</span>}</div>
                  <div className="truncate font-num text-[11px] text-[hsl(var(--copper))]">{u.email}</div>
                </div>
                {/* Move out (remove from this group) */}
                <Select value="" onValueChange={(gid) => moveUserToGroup(u, gid, groups.find((x) => x.id === gid)?.name || '')}>
                  <SelectTrigger className="h-8 w-36 shrink-0 text-xs" data-testid={`member-move-${u.email}`}>
                    <ArrowRightLeft size={12} className="mr-1 text-muted-foreground" /><span className="text-muted-foreground">Move to…</span>
                  </SelectTrigger>
                  <SelectContent>
                    {groups.filter((x) => x.id !== membersGroupId).map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
